import { createFeatureSelector, createSelector } from '@ngrx/store';
import { map, sortBy } from 'lodash';

import { ClassOfBusiness, T2Code } from '@lockton-apps/shared/interfaces';
import { t2CodeLite } from '@lockton-apps/client-gateway/transformations';

import {
  T2CodesState,
  T2_CODES_FEATURE_KEY,
  t2CodesAdapter,
} from './t2-codes.reducer';

// Lookup the 'T2Codes' feature state managed by NgRx
export const selectT2CodesState =
  createFeatureSelector<T2CodesState>(T2_CODES_FEATURE_KEY);

const { selectAll, selectEntities } = t2CodesAdapter.getSelectors();

export const selectT2CodesLoaded = createSelector(
  selectT2CodesState,
  (state: T2CodesState) => state.loaded
);

export const selectT2CodesError = createSelector(
  selectT2CodesState,
  (state: T2CodesState) => state.error
);

export const selectAllT2Codes = createSelector(
  selectT2CodesState,
  (state: T2CodesState) => selectAll(state)
);

export const selectAllT2CodesSortedByName = createSelector(
  selectAllT2Codes,
  (allT2Codes) =>
    sortBy(
      map(
        allT2Codes,
        (t2Code): T2Code => ({
          ...t2Code,
          classOfBusinesses: sortBy(t2Code.classOfBusinesses, 'name'),
        })
      ),
      'name'
    )
);

export const selectT2CodesEntities = createSelector(
  selectT2CodesState,
  (state: T2CodesState) => selectEntities(state)
);

export const selectAllT2CodesLight = createSelector(
  selectAllT2Codes,
  (t2Codes) => t2Codes.map(t2CodeLite)
);

export const selectAllT2CodesLightSortedByName = createSelector(
  selectAllT2Codes,
  (t2Codes) => sortBy(t2Codes.map(t2CodeLite), 'name')
);

export const selectAllT2CodesAndLoaded = createSelector(
  selectAllT2CodesSortedByName,
  selectT2CodesLoaded,
  (t2Codes, t2CodesLoaded) => ({ t2Codes, t2CodesLoaded })
);

export const selectAllT2CodesLightAndLoaded = createSelector(
  selectAllT2CodesLightSortedByName,
  selectT2CodesLoaded,
  (t2Codes, t2CodesLoaded) => ({ t2Codes, t2CodesLoaded })
);

export const selectAllCOBs = createSelector(
  selectT2CodesState,
  (state: T2CodesState) =>
    selectAll(state).reduce((cobs, t2Code) => {
      return [...cobs, ...t2Code.classOfBusinesses];
    }, [] as ClassOfBusiness[])
);

export const selectAllT2CodesAndCobsAndLoaded = createSelector(
  selectAllT2CodesSortedByName,
  selectAllCOBs,
  selectT2CodesLoaded,
  (t2Codes, cobs, t2CodesLoaded) => ({ t2Codes, cobs, t2CodesLoaded })
);

export const getSelectedId = createSelector(
  selectT2CodesState,
  (state: T2CodesState) => state.selectedId
);

export const getSelected = createSelector(
  selectT2CodesEntities,
  getSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

export const getSelectionViewModel = createSelector(
  selectAllT2CodesSortedByName,
  selectT2CodesLoaded,
  (t2Codes, loaded) => ({ t2Codes, loaded })
);

export const getCOBSelectionVm = createSelector(
  selectAllCOBs,
  selectT2CodesLoaded,
  (cobs, cobsLoaded) => ({ cobs, cobsLoaded })
);
